'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useState, useEffect } from 'react';
import {
  ReceiptText,
  HelpCircle,
  Activity,
  Settings,
  ChevronLeft,
  ChevronRight,
  Mail,
  Database,
  FolderOpen,
  Users,
  Truck,
  ClipboardCheck,
  BarChart2,
  Ship,
  LogOut,
  Shield,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useStatus } from '@/lib/hooks/use-status';
import { useAuthStore } from '@/lib/auth-store';
import { hatZugriffAufSeite } from '@/lib/page-permissions';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useI18n } from '@/lib/i18n';

const ICON_MAP = {
  ReceiptText,
  HelpCircle,
  Activity,
  Settings,
  Users,
  Truck,
  ClipboardCheck,
  BarChart2,
  Shield,
} as const;

const NAV_GROUPS = [
  {
    gruppe: 'Übersicht',
    items: [
      { href: '/auftragsbestaetigung', label: 'Auftragsbestätigungen', icon: 'ReceiptText' as const },
      { href: '/messberichte', label: 'Messberichte', icon: 'ClipboardCheck' as const },
      { href: '/lieferanten', label: 'Lieferanten', icon: 'Users' as const },
      { href: '/lieferungen', label: 'Lieferungen', icon: 'Truck' as const },
      { href: '/unklarheiten', label: 'Unklarheiten', icon: 'HelpCircle' as const },
    ],
  },
  {
    gruppe: 'System',
    items: [
      { href: '/reporting', label: 'Reporting', icon: 'BarChart2' as const },
      { href: '/aktivitaet', label: 'Aktivität', icon: 'Activity' as const },
      { href: '/steuerung', label: 'Steuerung', icon: 'Settings' as const },
      { href: '/benutzerverwaltung', label: 'Benutzer', icon: 'Shield' as const },
    ],
  },
];

const ADAPTER_ICONS = [
  { key: 'email' as const, Icon: Mail, label: 'E-Mail' },
  { key: 'erp' as const, Icon: Database, label: 'ERP' },
  { key: 'dms' as const, Icon: FolderOpen, label: 'DMS' },
  { key: 'vessel_tracking' as const, Icon: Ship, label: 'Vessel' },
];

const ROLLEN_LABEL: Record<string, string> = {
  admin: 'Admin',
  einkauf: 'Einkauf',
  qualitaet: 'Qualität',
  logistik: 'Logistik',
};

export function Sidebar() {
  const { t } = useI18n();
  const pathname = usePathname();
  const router = useRouter();
  const { data: status } = useStatus();
  const { benutzer, logout } = useAuthStore();
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('sidebar-collapsed');
    if (stored !== null) setCollapsed(stored === 'true');
  }, []);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('sidebar-collapsed', String(next));
  };

  const handleLogout = () => {
    logout();
    router.replace('/login');
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'flex h-screen flex-col border-r border-border bg-card transition-all duration-300',
          collapsed ? 'w-[68px]' : 'w-[260px]'
        )}
      >
        {/* Logo */}
        <div className="flex h-[60px] items-center gap-3 px-4">
          {!collapsed && (
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold tracking-tight">{t('Beschaffungsagent')}</p>
            </div>
          )}
        </div>

        <div className="mx-3 h-px bg-border" />

        {/* Navigation – gefiltert nach Rolle */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {NAV_GROUPS.map((group, gi) => {
            // Nur Items anzeigen, auf die der Benutzer Zugriff hat
            const sichtbareItems = group.items.filter((item) =>
              hatZugriffAufSeite(benutzer, item.href)
            );

            if (sichtbareItems.length === 0) return null;

            return (
              <div key={group.gruppe} className={cn(gi > 0 && 'mt-6')}>
                {!collapsed && (
                  <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/70">
                    {t(group.gruppe)}
                  </p>
                )}
                {collapsed && gi > 0 && <div className="mx-2 mb-3 h-px bg-border/40" />}
                <div className="space-y-0.5">
                  {sichtbareItems.map((item) => {
                    const Icon = ICON_MAP[item.icon];
                    const isActive = pathname === item.href;

                    const link = (
                      <Link
                        key={item.href}
                        href={item.href}
                        className={cn(
                          'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium transition-all',
                          isActive
                            ? 'bg-muted text-foreground'
                            : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                        )}
                      >
                        {isActive && (
                          <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-foreground" />
                        )}
                        <Icon className={cn('h-[18px] w-[18px] shrink-0', isActive && 'text-foreground')} />
                        {!collapsed && <span>{t(item.label)}</span>}
                      </Link>
                    );

                    if (collapsed) {
                      return (
                        <Tooltip key={item.href}>
                          <TooltipTrigger asChild>{link}</TooltipTrigger>
                          <TooltipContent side="right" className="text-xs">
                            {t(item.label)}
                          </TooltipContent>
                        </Tooltip>
                      );
                    }

                    return link;
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="mx-3 h-px bg-border" />

        {/* Benutzer-Info + Agent Status + Adapter Info */}
        <div className="p-3">
          {/* Benutzer-Info */}
          {benutzer && !collapsed && (
            <div className="mb-3 flex items-center justify-between rounded-lg bg-muted/30 px-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-xs font-medium">{benutzer.anzeigename}</p>
                <p className="text-[10px] text-muted-foreground">
                  {t(ROLLEN_LABEL[benutzer.rolle] || benutzer.rolle)}
                </p>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={handleLogout}
                  >
                    <LogOut className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="text-xs">{t('Abmelden')}</TooltipContent>
              </Tooltip>
            </div>
          )}

          {benutzer && collapsed && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="mb-2 h-8 w-full text-muted-foreground hover:text-destructive"
                  onClick={handleLogout}
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" className="text-xs">
                {benutzer.anzeigename} - {t('Abmelden')}
              </TooltipContent>
            </Tooltip>
          )}

          {/* Agent Status */}
          <div className={cn('flex items-center gap-2.5', collapsed ? 'justify-center' : 'px-2')}>
            <span className="relative flex h-2.5 w-2.5">
              <span
                className={cn(
                  'absolute inline-flex h-full w-full animate-ping rounded-full opacity-75',
                  status?.agent_laeuft ? 'bg-emerald-400' : 'bg-red-400'
                )}
              />
              <span
                className={cn(
                  'relative inline-flex h-2.5 w-2.5 rounded-full',
                  status?.agent_laeuft ? 'bg-emerald-400' : 'bg-red-400'
                )}
              />
            </span>
            {!collapsed && (
              <span className="text-xs font-medium text-muted-foreground">
                {status?.agent_laeuft ? t('Agent aktiv') : t('Agent gestoppt')}
              </span>
            )}
          </div>

          {!collapsed && status?.adapter && (
            <div className="mt-3 space-y-1 rounded-lg bg-muted/30 px-3 py-2">
              {ADAPTER_ICONS.map(({ key, Icon, label }) => {
                const adapterValue = (status.adapter as unknown as Record<string, string | undefined>)[key];
                if (!adapterValue) return null;
                return (
                  <div key={key} className="flex items-center justify-between text-[11px]">
                    <span className="flex items-center gap-1.5 text-muted-foreground/70">
                      <Icon className="h-3 w-3" />
                      {t(label)}
                    </span>
                    <span className="font-mono text-muted-foreground">
                      {adapterValue}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={toggleCollapsed}
            className="mt-2 h-8 w-full justify-center text-muted-foreground/60 hover:text-foreground"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>
      </aside>
    </TooltipProvider>
  );
}
